import {
  bottomLevelFacetFragment,
  midLevelFacetFragment,
  topLevelFacetFragment,
  filterInvertedToggleFragment,
  headerFragment,
  filterToggleFragment,
  filterIntegerFragment,
} from '../filters/__generated__/fragments'
import {
  FilterHeader,
  FilterCategory,
  ApplicableFilter,
  IntegerFilter,
} from './services'

type FilterFacetGqlType = Overwrite<
  topLevelFacetFragment | midLevelFacetFragment | bottomLevelFacetFragment,
  {
    children?:
      | topLevelFacetFragment['children']
      | midLevelFacetFragment['children']
      | null
  }
>

export type FilterItemGqlType =
  | FilterFacetGqlType
  | filterToggleFragment
  | filterInvertedToggleFragment
  | headerFragment
  | filterIntegerFragment

const mapGqlFilterFacetToDomain = (
  gqlFilter:
    | FilterFacetGqlType
    | filterToggleFragment
    | filterInvertedToggleFragment
    | filterIntegerFragment,
): FilterCategory | ApplicableFilter | IntegerFilter => {
  if (
    gqlFilter.__typename === 'FilterFacet' &&
    gqlFilter.children &&
    gqlFilter.children.length
  ) {
    const children = gqlFilter.children.map(child =>
      mapGqlFilterItemToDomain(child as FilterItemGqlType),
    )

    return {
      __typename: 'FilterCategory',
      id: gqlFilter.facetKey,
      parent: gqlFilter.group,
      directParentKey: gqlFilter.parentKey,
      count: gqlFilter.count,
      label: gqlFilter.label,
      nbaScore: gqlFilter.nbaScore || undefined,
      isSelected: gqlFilter.isUserSelected,
      isQuickFilter: !!gqlFilter.isQuickFilter,
      hasSelectedChildren: gqlFilter.hasSelectedChildren,
      children,
    }
  }

  if (gqlFilter.__typename === 'ToggleFilter') {
    return {
      __typename: 'FilterToggle',
      id: gqlFilter.facetKey,
      parent: gqlFilter.group,
      isSelected: gqlFilter.isUserSelected,
      isQuickFilter: !!gqlFilter.isQuickFilter,
      label: gqlFilter.label,
      ...(gqlFilter.icon && { icon: gqlFilter.icon }),
    }
  }

  if (gqlFilter.__typename === 'InvertedToggleFilter') {
    return {
      __typename: 'FilterInvertedToggle',
      id: gqlFilter.facetKey,
      parent: gqlFilter.group,
      isSelected: gqlFilter.isUserSelected,
      isQuickFilter: !!gqlFilter.isQuickFilter,
      label: gqlFilter.label,
      ...(gqlFilter.icon && /* istanbul ignore next */ {
        icon: gqlFilter.icon,
      }),
    }
  }

  if (gqlFilter.__typename === 'IntegerFilter') {
    return {
      __typename: 'IntegerFilter',
      label: gqlFilter.label,
      parent: `integer-${gqlFilter.group}`,
      integerId: gqlFilter.integerFilterType,
      integerKey: gqlFilter.integerFilterKey,
      id: `integer-${gqlFilter.group}`,
      selectedValue: gqlFilter.selectedValue,
      selectedValueLabel: gqlFilter.selectedValueLabel,
      isSelected: gqlFilter.isSelected,
      isNew: true,
      isQuickFilter: !!gqlFilter.isQuickFilter,
      min: gqlFilter.min,
      max: gqlFilter.max,
    }
  }

  return {
    __typename: 'Filter',
    id: gqlFilter.facetKey,
    parent: gqlFilter.group,
    count: gqlFilter.count,
    isSelected: gqlFilter.isUserSelected,
    label: gqlFilter.label,
    nbaScore: gqlFilter.nbaScore || undefined,
    directParentKey: gqlFilter.parentKey,
  }
}

const mapGqlFilterHeaderToDomain = (
  gqlFacet: headerFragment,
): FilterHeader => ({
  __typename: gqlFacet.__typename,
  label: gqlFacet.name,
})

export const mapGqlFilterItemToDomain = (
  gqlFacet: FilterItemGqlType,
): ApplicableFilter | FilterCategory | FilterHeader | IntegerFilter => {
  return gqlFacet.__typename === 'FilterFacet' ||
    gqlFacet.__typename === 'ToggleFilter' ||
    gqlFacet.__typename === 'InvertedToggleFilter' ||
    gqlFacet.__typename === 'IntegerFilter'
    ? mapGqlFilterFacetToDomain(gqlFacet)
    : mapGqlFilterHeaderToDomain(gqlFacet)
}
